import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Film, Mic, PlayCircle } from "lucide-react";
import PublicShell from "../../components/PublicShell";
import EpisodeList from "../../components/podcast/EpisodeList";
import api from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";
import { useAuth } from "../../context/AuthContext";
import ShareKit from "../../components/ShareKit";

const SITE = "https://thedialstream.com";
const clip = (s, n = 155) => (s && s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s || "");

function metaFor(kind, d, slug, episodes) {
  if (!d) return {};
  const url = `${SITE}/${kind}/${slug}`;
  if (kind === "indie") {
    const label = d.year ? `${d.title} (${d.year})` : d.title;
    return {
      title: `${label} — Independent Film | Where to Watch | The Dial`,
      description: clip(`Watch ${label}${d.director ? `, an independent film by ${d.director}` : ""}. ${d.overview || ""}`),
      jsonLd: { "@context": "https://schema.org", "@type": "Movie", name: d.title, url, image: d.poster || undefined,
        datePublished: d.year ? String(d.year) : undefined, genre: d.genres?.length ? d.genres : undefined,
        description: d.overview || undefined, director: d.director ? { "@type": "Person", name: d.director } : undefined,
        potentialAction: d.indie_watch?.url ? { "@type": "WatchAction", target: d.indie_watch.url } : undefined },
    };
  }
  return {
    title: `${d.title} — TV & Film Podcast | The Dial`,
    description: clip(`${d.title}${d.host ? `, hosted by ${d.host}` : ""}. ${d.description || ""}`),
    jsonLd: { "@context": "https://schema.org", "@type": "PodcastSeries", name: d.title, url, image: d.poster || undefined,
      description: d.description || undefined, author: d.host ? { "@type": "Person", name: d.host } : undefined,
      sameAs: d.url || undefined,
      hasPart: episodes.length ? episodes.map((e) => ({ "@type": "PodcastEpisode", name: e.title, url: e.url })) : undefined },
  };
}

function Byline({ d, indie }) {
  const text = indie
    ? [d.director && `Directed by ${d.director}`, d.runtime && `${d.runtime} min`, d.genres?.join(" · ")].filter(Boolean).join(" · ")
    : d.host && `Hosted by ${d.host}`;
  return text ? <p className="mt-2 text-sm text-[var(--text-secondary)]" data-testid="public-byline">{text}</p> : null;
}

function Actions({ d, indie, slug, user }) {
  const link = indie ? d.indie_watch : { url: d.url, label: d.url_label };
  const ghost = "inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]";
  return (
    <div className="mt-7 flex flex-wrap gap-3">
      {link?.url && <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" data-testid="public-watch-link"><ExternalLink size={18} /> {link.label || (indie ? "Watch now" : "Listen now")}</a>}
      {indie && d.trailer_url && <a href={d.trailer_url} target="_blank" rel="noopener noreferrer" className={ghost} data-testid="public-trailer-link"><PlayCircle size={17} /> Trailer</a>}
      <Link to={user ? (indie ? `/title/indie-${slug}` : `/podcast/${slug}`) : "/register"} className={ghost} data-testid="public-open-in-app">{user ? "Open in The Dial" : "Join The Dial free"}</Link>
      <ShareKit kind={indie ? "indie" : "podcasts"} slug={slug} title={d.title} />
    </div>
  );
}

export default function SpotlightDetail({ kind }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const indie = kind === "indie";
  useEffect(() => {
    window.scrollTo(0, 0); setD(null); setMissing(false); setEpisodes([]);
    api.get(indie ? `/public/indie/${slug}` : `/podcasts/${slug}`).then(({ data }) => setD(data)).catch(() => setMissing(true));
    if (!indie) api.get(`/podcasts/${slug}/episodes`).then(({ data }) => setEpisodes(data.episodes || [])).catch(() => {});
  }, [slug, indie]);
  usePageMeta(metaFor(kind, d, slug, episodes));
  useEffect(() => {
    if (!missing) return undefined;
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex";
    document.head.appendChild(m);
    return () => m.remove();
  }, [missing]);
  const Icon = indie ? Film : Mic;

  return (
    <PublicShell testid={`public-${kind}-detail`}>
      <nav className="text-xs text-[var(--text-muted)]"><Link to="/" className="hover:text-[var(--text-primary)]">Home</Link> › <Link to={`/${kind}`} className="hover:text-[var(--text-primary)]" data-testid="public-crumb-index">{indie ? "Indie Spotlight" : "Podcast Spotlight"}</Link>{d ? ` › ${d.title}` : ""}</nav>
      {missing && <p className="mt-10 text-[var(--text-secondary)]" data-testid="public-not-found">This {indie ? "film" : "podcast"} isn't on The Dial. <Link to={`/${kind}`} className="text-[var(--accent-hover)] underline">See all</Link></p>}
      {d && (
        <div className="mt-6 flex flex-col gap-8 sm:flex-row">
          <div className="w-full max-w-[260px] flex-none self-center sm:self-start">
            {d.poster
              ? <img src={d.poster} alt={`${d.title} ${indie ? "poster" : "cover art"}`} className={`${indie ? "aspect-[2/3]" : "aspect-square"} w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] object-cover shadow-xl`} data-testid="public-poster" />
              : <div className="flex aspect-square w-full items-center justify-center rounded-[var(--card-radius)] bg-[var(--bg-card)] text-[var(--text-muted)]"><Icon size={48} /></div>}
          </div>
          <div className="min-w-0 flex-1">
            <span className="inline-block rounded-sm bg-[var(--accent)] px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--accent-contrast)]" data-testid="public-badge">{indie ? "Indie" : "Podcast"}</span>
            <h1 className="mt-3 break-words font-head text-4xl tracking-wide text-[var(--text-primary)] sm:text-5xl" data-testid="public-title">{d.title}{indie && d.year ? <span className="text-[var(--text-muted)]"> ({d.year})</span> : null}</h1>
            <Byline d={d} indie={indie} />
            <p className="mt-5 max-w-2xl whitespace-pre-wrap break-words leading-relaxed text-[var(--text-secondary)]" data-testid="public-description">{indie ? d.overview : d.description}</p>
            <Actions d={d} indie={indie} slug={slug} user={user} />
            <p className="mt-3 text-xs text-[var(--text-muted)]">The Dial lists and links out — it never hosts {indie ? "films" : "podcasts"}.</p>
          </div>
        </div>
      )}
      {d && !indie && episodes.length > 0 && (
        <section className="mt-12"><h2 className="font-head text-2xl text-[var(--text-primary)]">Episodes</h2><EpisodeList episodes={episodes} /></section>
      )}
    </PublicShell>
  );
}
